import type ContractDraftContext from '../../drafts/ContractDraftContext';
import { makeObservable, observable, action, computed } from 'mobx';
import { LayersWithSectionsNodeDto, SectionSyncRuleDto } from '../../types';
import { NodeBaseModel, LayerWithSectionsModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';
import { SectionSyncManager } from '../../misc';

export default class LayersWithSectionsNodeModel extends NodeBaseModel {
    layers: LayerWithSectionsModel[] = [];

    readonly sectionSyncRules: SectionSyncRuleDto[] | null;

    readonly sectionSyncManager: SectionSyncManager;

    constructor(data: LayersWithSectionsNodeDto, sources: SourceModelBase[], context: ContractDraftContext) {
        super(data, context);

        this.sectionSyncRules = data.sectionSyncRules;
        this.sectionSyncManager = new SectionSyncManager(
            this.sectionSyncRules,
            input => this.getLayerByInputGuid(input.guid)?.sections ?? []
        );

        this.layers = data.layers.map(layer => new LayerWithSectionsModel(this, layer, sources));

        makeObservable(this, {
            layers: observable,
            inputs: computed,
            update: action,
            filterLayers: action
        });
    }

    refreshExpandedState(layerId?: string) {
        this.layers.forEach(layer => {
            if (layerId === undefined || layer.id === layerId) {
                layer.refreshExpandedState();
            }
        });
    }

    getLayerByInputGuid(guid: string) {
        for (const layer of this.layers) {
            if (layer.sections.some(s => s.inputs.some(i => i.guid === guid))) {
                return layer;
            }
        }

        return null;
    }

    update(data: LayersWithSectionsNodeDto, sources: SourceModelBase[]) {
        data.layers.forEach(layer => {
            const existingLayer = this.layers.find(l => l.id === layer.id);

            if (!existingLayer) {
                this.layers.push(new LayerWithSectionsModel(this, layer, sources));
            } else {
                existingLayer.update(layer, sources);
            }
        });

        this.layers = this.layers.filter(layer => data.layers.some(l => l.id === layer.id));
    }

    getInputsAtSameLevel(inputGuid: string) {
        const layer = this.getLayerByInputGuid(inputGuid);
        const section = layer?.getSectionByInputGuid(inputGuid);
        return section ? section.inputs : [];
    }

    filterLayers(layerIds: string[]) {
        this.layers = this.layers.filter(layer => layerIds.includes(layer.id));
    }

    get inputs() {
        return this.layers.flatMap(layer => layer.sections.flatMap(section => section.inputs));
    }

    get dto(): LayersWithSectionsNodeDto {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            inputAvailability: this.inputAvailability,
            layers: this.layers.map(layer => layer.dto),
            childNodes: this.childNodes.map(node => node.dto),
            sectionSyncRules: this.sectionSyncRules
        };
    }
}
